import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GmailState, Mailbox } from '@/types';
import { request } from './helper';

// **Thunks**
export const gmailGetLoginUrl = createAsyncThunk<string>(
  'gmail/getLoginUrl',
  async () => {
    const response = await request('admin/gmail/get-login-url');
    return response.success ? response.data : null;
  }
);

export const gmailSaveMailbox = createAsyncThunk<boolean, Partial<Mailbox>>(
  'gmail/saveMailbox',
  async (data) => {
    const response = await request('admin/gmail/save-mailbox', 'POST', data);
    return response.success;
  }
);

export const gmailGetMailboxes = createAsyncThunk<Mailbox[]>(
  'gmail/getMailboxes',
  async () => {
    const response = await request('admin/gmail/all-mailboxes');
    return response.success ? response.data : [];
  }
);

export const gmailDeleteMailbox = createAsyncThunk<string, string>(
  'gmail/deleteMailbox',
  async (mailbox) => {
    const response = await request(`admin/gmail/remove-mailbox/${mailbox}`, 'DELETE');
    return response.success ? mailbox : ''; // Return mailbox on success
  }
);

// **Initial State**
const initialState: GmailState = {
  loginUrl: null,
  mailboxes: [],
  status: 'idle',
  mailboxesStatus: 'idle',
};

// **Slice**
const gmailSlice = createSlice({
  name: 'gmail',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const setLoading = (state: GmailState) => {
      state.status = 'loading';
    };
    const setFailed = (state: GmailState) => {
      state.status = 'failed';
    };

    // Get Login URL
    builder
      .addCase(gmailGetLoginUrl.fulfilled, (state, { payload }: PayloadAction<string | null>) => {
        state.loginUrl = payload;
      });

    // Save Mailbox
    builder
      .addCase(gmailSaveMailbox.pending, setLoading)
      .addCase(gmailSaveMailbox.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(gmailSaveMailbox.rejected, setFailed);

    // Get Mailboxes
    builder
      .addCase(gmailGetMailboxes.pending, (state) => {
        state.mailboxesStatus = 'loading';
      })
      .addCase(gmailGetMailboxes.fulfilled, (state, { payload }: PayloadAction<Mailbox[]>) => {
        state.mailboxes = payload;
        state.mailboxesStatus = 'success';
      })
      .addCase(gmailGetMailboxes.rejected, (state) => {
        state.mailboxesStatus = 'failed';
      });

    // Delete Mailbox
    builder
      .addCase(gmailDeleteMailbox.fulfilled, (state, { payload }) => {
        if (payload) {
          state.mailboxes = state.mailboxes.filter((item) => item.mailbox !== payload);
        }
      });
  },
});

// **Export Reducer**
export default gmailSlice.reducer;